import { getRange } from '../../helpers/axisDef';
import {
  renderGrid,
  renderXAxisLabels,
  renderYaxisNumerical,
  renderVerticalStripDividers,
} from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getStandardDataColor } from '../../../themes/themes';
import { renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorLegendInfo,
  getMappedColorStringDataPoint,
} from '../../helpers/colorMappingInfo';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { getThemedGrayFraction } from '../../../themes/themes';
import { sortCategories, TpCategoryInfo } from '../../helpers/categoricalSorters';
import { mulberry32 } from '../../../../components/randomGenerators';
import { PERFORMANCE_POINT_COUNT_THRESHOLD } from '../config';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

interface TpStripInfo extends TpCategoryInfo {
  valueSum: number;
  valueAverage: number;
  positionIndex: number;
  memberItemNumbers: number[];
  memberItemHPosits: number[];
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataCategories = boundData.bindings.categories.data;
  const colInfoCats = boundData.bindings.categories.columnInfo;
  const dataValues = boundData.bindings.values.data;
  const dataItemId = boundData.bindings.itemId.data;
  const hoverLabelColInfo = boundData.bindings.hoverLabel?.columnInfo;
  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const nameX = boundData.bindings.categories.columnInfo.name;
  const nameY = boundData.bindings.values.columnInfo.name;
  const title = generateChartTitle(dataContext, dataBinding, config, `${nameX} vs ${nameY}`);

  const itemCount = dataValues.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const showEmptyStrips = config.emptyStrips;

  const opacity = config.opacity;
  const unselectedOpacity = config.unselectedOpacity;
  const pointSizeFactor = config.pointSizeFactor;

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);

  const rnd = mulberry32(0);

  const itemHPosits: number[] = [];

  const stripMap: { [cat: string]: TpStripInfo } = {};
  let strips: TpStripInfo[] = [];
  for (let itemNr = 0; itemNr < itemCount; itemNr++) {
    const catValue = dataCategories[itemNr];
    if (!stripMap[catValue]) {
      const cat: TpStripInfo = {
        value: catValue,
        displayString: convertColumnDataToDisplayString(colInfoCats, catValue),
        count: 0,
        valueSum: 0,
        valueAverage: 0,
        countSelected: 0,
        positionIndex: 0,
        memberItemNumbers: [],
        memberItemHPosits: [],
      };
      strips.push(cat);
      stripMap[catValue] = cat;
    }
    const strip = stripMap[catValue];
    const offset = 0.2 + 0.6 * rnd();
    if (!isNaN(dataValues[itemNr])) {
      strip.count++;
      strip.valueSum += dataValues[itemNr];
      if (isSelectedItem(selectedItemsMap, dataItemId[itemNr])) stripMap[catValue].countSelected++;
    }
    strip.memberItemNumbers.push(itemNr);
    strip.memberItemHPosits.push(offset);
    itemHPosits.push(offset);
  }

  for (const cat of strips) cat.valueAverage = cat.valueSum / cat.count;

  // Filter out empty strips if the config option is not set to show them, and determine category order
  if (!showEmptyStrips) {
    strips = strips.filter((strip) => strip.count > 0);
  }

  const catCount = strips.length;
  sortCategories(strips, config.sortBy);
  if (config.sortBy == 'avgValInc') strips.sort((a, b) => a.valueAverage - b.valueAverage);
  if (config.sortBy == 'avgValDec') strips.sort((a, b) => b.valueAverage - a.valueAverage);

  const catLabels = strips.map((cat) => cat.displayString);

  let posIndex = 0;
  for (const cat of strips) {
    cat.positionIndex = posIndex;
    posIndex++;
  }

  for (let i = 0; i < itemCount; i++)
    itemHPosits[i] = (stripMap[dataCategories[i]].positionIndex + itemHPosits[i]) / catCount;

  const [minVal, maxVal] = getRange(dataValues, 0.05);

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, catLabels);
  }

  function renderStrips(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    renderGrid(renderContext, false, true);
    renderVerticalStripDividers(strips.length, renderContext);
    ctx.lineWidth = renderContext.pixelRatio * 1;

    const standardFillColor = getStandardDataColor().opacity(opacity).toCanvasString();
    const grayColor = getThemedGrayFraction(0.5)
      .opacity(unselectedOpacity * opacity)
      .toCanvasString();
    ctx.fillStyle = standardFillColor;
    ctx.strokeStyle = getStandardDataColor()
      .opacity(opacity * 0.3)
      .toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 0.75;

    const fastRenderPoint = itemCount > PERFORMANCE_POINT_COUNT_THRESHOLD;
    const pointSizeRect = Math.round(pixelConv(2.5 * pointSizeFactor));

    for (const strip of strips) {
      const stripPositionIndex = strip.positionIndex;

      for (let i = 0; i < strip.memberItemNumbers.length; i++) {
        const itemNr = strip.memberItemNumbers[i];
        const hPosit = strip.memberItemHPosits[i];
        const scr_x = mapx((stripPositionIndex + hPosit) / catCount);
        const scr_y = mapy(dataValues[itemNr]);
        const isSelected = isSelectedItem(selectedItemsMap, dataItemId[itemNr]);

        if (colorMappingInfo.hasColors) {
          const [fillColorStr, strokeColorStr] = getMappedColorStringDataPoint(colorMappingInfo, itemNr);
          ctx.fillStyle = fillColorStr;
          ctx.strokeStyle = strokeColorStr;
        } else ctx.fillStyle = standardFillColor;

        if (fastRenderPoint) {
          if (!isSelectedItem(selectedItemsMap, dataItemId[itemNr])) ctx.fillStyle = grayColor;
          ctx.fillRect(
            Math.round(scr_x - pointSizeRect / 2),
            Math.round(scr_y - pointSizeRect / 2),
            pointSizeRect,
            pointSizeRect,
          );
        } else {
          const pointSize = 3;
          ctx.beginPath();
          ctx.arc(scr_x, scr_y, pixelConv(pointSize * pointSizeFactor), 0, 2 * Math.PI);
          if (isSelected) {
            ctx.fill();
            ctx.stroke();
          } else {
            ctx.strokeStyle = grayColor;
            ctx.stroke();
          }
        }
      }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  const hasLegend = colorMappingInfo.hasColors && !config.hideColorLegend;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpace);

  const zpLogicalRangeX = createLogicalRange(0, 1); //-0.5 / catCount, 1 + 0.5 / catCount);
  const zpLogicalRangeY = createLogicalRange(minVal, maxVal);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'strips',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'y',
    render: renderStrips,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, nameY, title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataCategories,
    dataValues,
    hoverLabelColInfo,
    dataHoverLabel,
    itemHPosits,
    catColumnId: boundData.bindings.categories.columnId,
    colInfoValues: boundData.bindings.values.columnInfo,
    colInfoCategories: boundData.bindings.categories.columnInfo,
    nameX,
    nameY,
    itemCount,
    dataItemId,
    strips,
    stripMap,
    getItemIndexById: boundData.getItemIndexById,
    title,
  };
}
